import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { type OrganizationManager } from "@entities";
import { organizationManagerRepo } from "@repositories";
import { linko } from "@shared/linko";
import { Roles } from "@shared/Roles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FC, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { remult } from "remult";
import { useAlert } from "../../contexts/AlertProvider";
import { useUser } from "../../contexts/UserProvider";
import OpeningHubOptionsEditor, {
  MAX_OPENING_HUB_OPTIONS,
  isOpeningHubIconKey,
  isSafeInternalPath,
  normalizeInternalPath,
  normalizeOpeningHubAccentColor,
  type OpeningHubOptionFormItem,
} from "./OpeningHubOptionsEditor";

type OpeningHubFormState = {
  isEmergencyMode: boolean;
  emergencyTitle: string;
  options: OpeningHubOptionFormItem[];
  editorUserIds: string[];
};

function mapConfigOptions(
  raw: OpeningHubOptionFormItem[] | undefined,
): OpeningHubOptionFormItem[] {
  if (!raw?.length) return [];
  return raw.map((opt) => ({
    id: opt.id || crypto.randomUUID(),
    label: opt.label ?? "",
    description: opt.description ?? "",
    path: opt.path ?? "",
    iconKey: isOpeningHubIconKey(opt.iconKey) ? opt.iconKey : "handshake",
    accentColor: normalizeOpeningHubAccentColor(opt.accentColor),
    enabled: !!opt.enabled,
  }));
}

function validateOptions(options: OpeningHubOptionFormItem[]): string | null {
  if (options.length === 0) {
    return "יש להוסיף לפחות אפשרות אחת";
  }
  if (options.length > MAX_OPENING_HUB_OPTIONS) {
    return `ניתן להגדיר עד ${MAX_OPENING_HUB_OPTIONS} אפשרויות`;
  }
  if (!options.some((o) => o.enabled)) {
    return "יש להשאיר לפחות אפשרות אחת מופעלת";
  }

  const seenPaths = new Set<string>();
  for (const option of options) {
    if (!option.label.trim()) {
      return "לכל אפשרות חייבת להיות כותרת";
    }
    const path = normalizeInternalPath(option.path);
    if (!path) {
      return "לכל אפשרות חייב להיות נתיב";
    }
    if (!isSafeInternalPath(path)) {
      return "נתיב לא תקין - יש להזין נתיב פנימי באפליקציה בלבד";
    }
    if (seenPaths.has(path)) {
      return "נתיבי האפשרויות חייבים להיות ייחודיים";
    }
    seenPaths.add(path);
  }
  return null;
}

const OpeningHubSettingsPage: FC = () => {
  const { currentUser } = useUser();
  const { setAlertText, setSeverity, setTime } = useAlert();
  const queryClient = useQueryClient();
  const isSuperDuper = remult.isAllowed(Roles.SuperDuperAdmin);

  const { data: config, isLoading: isConfigLoading, isError: isConfigError, refetch: refetchConfig } = useQuery({
    queryKey: ["opening-hub-config"],
    queryFn: async () => {
      const { data } = await linko.openingHubConfig.queries.getConfig();
      return data;
    },
  });

  const { data: access, isLoading: isAccessLoading, isError: isAccessError, refetch: refetchAccess } = useQuery({
    queryKey: ["opening-hub-config-access", currentUser?.id],
    queryFn: async () => {
      const { data } = await linko.openingHubConfig.queries.getAccess();
      return data;
    },
    enabled: !!currentUser?.id,
  });

  const { data: managers = [] } = useQuery({
    queryKey: ["opening-hub-config-managers", access?.editorUserIds],
    queryFn: async () => {
      const editorIds = access?.editorUserIds ?? [];
      const [listed, selected] = await Promise.all([
        organizationManagerRepo.find({
          orderBy: { name: "asc" },
          limit: 200,
        }),
        editorIds.length
          ? organizationManagerRepo.find({
              where: { id: { $in: editorIds } },
            })
          : Promise.resolve([] as OrganizationManager[]),
      ]);
      const byId = new Map<string, OrganizationManager>();
      for (const manager of [...listed, ...selected]) {
        byId.set(manager.id, manager);
      }
      return [...byId.values()];
    },
    enabled: isSuperDuper,
  });

  const [managerSearch, setManagerSearch] = useState("");
  const { data: searchedManagers = [] } = useQuery({
    queryKey: ["opening-hub-manager-search", managerSearch],
    queryFn: async () => {
      const q = managerSearch.trim();
      return organizationManagerRepo.find({
        where: {
          $or: [{ name: { $contains: q } }, { phoneNumber: { $contains: q } }],
        },
        orderBy: { name: "asc" },
        limit: 30,
      });
    },
    enabled: isSuperDuper && managerSearch.trim().length >= 2,
  });

  const [form, setForm] = useState<OpeningHubFormState | null>(null);

  useEffect(() => {
    if (!config || isAccessLoading) return;
    setForm((prev) => {
      // Keep in-progress edits; only hydrate once when form is empty.
      if (prev) return prev;
      return {
        isEmergencyMode: config.isEmergencyMode,
        emergencyTitle: config.emergencyTitle,
        options: mapConfigOptions(config.options),
        editorUserIds: access?.editorUserIds ?? [],
      };
    });
  }, [config, isAccessLoading, access?.editorUserIds]);

  const selectedEditors = useMemo(() => {
    if (!form) return [];
    const selected = new Set(form.editorUserIds);
    const byId = new Map<string, OrganizationManager>();
    for (const manager of [...managers, ...searchedManagers]) {
      byId.set(manager.id, manager);
    }
    return form.editorUserIds
      .map((id) => byId.get(id))
      .filter((m): m is OrganizationManager => !!m && selected.has(m.id));
  }, [form, managers, searchedManagers]);

  const managerOptions = useMemo(() => {
    const byId = new Map<string, OrganizationManager>();
    for (const manager of [...managers, ...searchedManagers, ...selectedEditors]) {
      byId.set(manager.id, manager);
    }
    return [...byId.values()];
  }, [managers, searchedManagers, selectedEditors]);

  const patchForm = <K extends keyof OpeningHubFormState>(
    key: K,
    value: OpeningHubFormState[K],
  ) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const saveMutation = useMutation({
    mutationFn: async (values: OpeningHubFormState) => {
      const options = values.options.map((option) => ({
        ...option,
        label: option.label.trim(),
        description: option.description.trim(),
        path: normalizeInternalPath(option.path),
      }));
      const { data } = await linko.openingHubConfig.mutations.updateConfig({
        isEmergencyMode: values.isEmergencyMode,
        emergencyTitle: values.emergencyTitle,
        options,
        // Never send editors when access did not load - avoids wiping allowlist.
        ...(isSuperDuper && access?.canEditEditors
          ? { editorUserIds: values.editorUserIds }
          : {}),
      });
      return data;
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(["opening-hub-config"], saved);
      queryClient.invalidateQueries({ queryKey: ["opening-hub-config-access"] });
      setForm((prev) =>
        prev
          ? {
              ...prev,
              isEmergencyMode: saved.isEmergencyMode,
              emergencyTitle: saved.emergencyTitle,
              options: mapConfigOptions(saved.options),
            }
          : prev,
      );
      setSeverity("success");
      setTime(2000);
      setAlertText("הגדרות מסך הפתיחה נשמרו");
    },
    onError: () => {
      setSeverity("warning");
      setTime(2500);
      setAlertText("שמירה נכשלה - בדקו הרשאות ונסו שוב");
    },
  });

  const handleSave = () => {
    if (!form) return;
    const validationError = validateOptions(form.options);
    if (validationError) {
      setSeverity("warning");
      setTime(3000);
      setAlertText(validationError);
      return;
    }
    saveMutation.mutate(form);
  };

  if (isConfigError || isAccessError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <Stack spacing={2} alignItems="center">
          <Typography color="text.secondary">
            לא הצלחנו לטעון את הגדרות מסך הפתיחה
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              void refetchConfig();
              void refetchAccess();
            }}
          >
            נסו שוב
          </Button>
        </Stack>
      </Box>
    );
  }

  if (isConfigLoading || isAccessLoading || !form) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSuperDuper && !access?.canEdit) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 3, dir: "rtl", overflow: "auto" }}>
      <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <WarningAmberRoundedIcon color="warning" />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                מסך פתיחה ומצב חירום
              </Typography>
              <Typography color="text.secondary" fontSize="0.9rem">
                שינויים נכנסים לתוקף מיד באפליקציה, בלי שחרור גירסה
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={700}>
              מצב חירום
            </Typography>
            <Typography color="text.secondary" fontSize="0.85rem">
              במצב חירום משתנים כותרת ועיצוב, מוסתרת אפשרות קיץ, ומוצגות קיצורי
              דרך לקטגוריות חירום (זירות נפילה, מלונות, הפעלות במקלטים).
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isEmergencyMode}
                  onChange={(_, checked) =>
                    patchForm("isEmergencyMode", checked)
                  }
                />
              }
              label="הפעלת מצב חירום במסך הפתיחה"
            />
            <TextField
              label="כותרת במצב חירום"
              value={form.emergencyTitle}
              onChange={(e) => patchForm("emergencyTitle", e.target.value)}
              fullWidth
            />
          </Stack>

          <Divider />

          <OpeningHubOptionsEditor
            options={form.options}
            onChange={(options) => patchForm("options", options)}
          />

          {isSuperDuper && (
            <>
              <Divider />
              <Stack spacing={2}>
                <Typography variant="subtitle1" fontWeight={700}>
                  מי יכול לערוך
                </Typography>
                <Typography color="text.secondary" fontSize="0.85rem">
                  Super Duper Admin תמיד יכול. הוסיפו מנהלים ספציפיים לרשימה.
                </Typography>
                <Autocomplete
                  multiple
                  options={managerOptions}
                  value={selectedEditors}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.phoneNumber})`
                  }
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  onChange={(_, value) =>
                    patchForm(
                      "editorUserIds",
                      value.map((m) => m.id),
                    )
                  }
                  onInputChange={(_, value) => setManagerSearch(value)}
                  filterOptions={(opts) => opts}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="עורכים מורשים"
                      placeholder="הקלד לחיפוש מנהל"
                    />
                  )}
                />
              </Stack>
            </>
          )}

          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              disabled={saveMutation.isPending}
              onClick={handleSave}
            >
              {saveMutation.isPending ? "שומר..." : "שמירה"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Container>
  );
};

export default OpeningHubSettingsPage;
